"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";

const POWER_WORDS = new Set([
  "proven",
  "essential",
  "ultimate",
  "complete",
  "definitive",
  "guaranteed",
  "exclusive",
  "instant",
  "instantly",
  "effortless",
  "easy",
  "simple",
  "quick",
  "fast",
  "free",
  "new",
  "best",
  "top",
  "expert",
  "professional",
  "powerful",
  "practical",
  "actionable",
  "step-by-step",
  "smart",
  "secret",
  "secrets",
  "insider",
  "hidden",
  "little-known",
  "unlock",
  "boost",
  "improve",
  "master",
  "win",
  "save",
  "avoid",
  "stop",
  "start",
  "now",
  "today",
  "results",
  "success",
  "successful",
  "effective",
  "efficient",
  "valuable",
  "premium",
  "advanced",
  "beginner",
  "checklist",
  "guide",
  "tips",
  "tricks",
  "hacks",
  "strategies",
  "mistakes",
  "reasons",
  "ways",
  "lessons",
]);

const EMOTIONAL_WORDS = new Set([
  "surprising",
  "shocking",
  "stunning",
  "amazing",
  "incredible",
  "unbelievable",
  "remarkable",
  "extraordinary",
  "jaw-dropping",
  "mind-blowing",
  "heartwarming",
  "heartbreaking",
  "inspiring",
  "inspirational",
  "uplifting",
  "devastating",
  "terrifying",
  "frightening",
  "scary",
  "alarming",
  "outrageous",
  "controversial",
  "brilliant",
  "genius",
  "beautiful",
  "gorgeous",
  "delightful",
  "hilarious",
  "funny",
  "joyful",
  "thrilling",
  "exciting",
  "fascinating",
  "captivating",
  "irresistible",
  "unforgettable",
  "epic",
  "legendary",
  "magical",
  "wonderful",
  "painful",
  "brutal",
  "fearless",
  "bold",
  "daring",
  "dangerous",
  "risky",
  "crazy",
  "wild",
  "weird",
]);

/*
 * Word boundaries by Unicode property, not by `a-z`.
 *
 * These three were ASCII classes, which is invisible in English and catastrophic in the
 * sixteen languages this tool is now published in. `[^a-z0-9'-]` treats every character
 * outside ASCII as a separator, so "Десять способов писать" and "عشر طرق للكتابة" split
 * into nothing at all: word count, power words, emotional words and the keyword-stuffing
 * check are four of the seven factors and 65 of the 100 points, and all four silently
 * scored zero for any headline not written in ASCII.
 *
 * Accented Latin was corrupted rather than emptied, which is worse because it looks like
 * it worked: "Grüße aus Köln" scored as five words and "Đây là tiêu đề" as the four
 * fragments "y", "l", "ti", "u".
 *
 * `\p{M}` is in the class alongside `\p{L}`: Devanagari writes its vowels as combining
 * marks, so without it every matra is a word boundary and "सुर्खियाँ लिखने के तरीके"
 * counts as nine words instead of four. Arabic and Vietnamese happen to survive without
 * it — Arabic short vowels are usually unwritten and Vietnamese tone marks are
 * precomposed — which is exactly why testing one non-Latin script is not enough.
 *
 * Japanese and Chinese are still counted as one word per unbroken run, because they do
 * not put spaces between words and no regex can segment them. `Intl.Segmenter` can, and
 * was considered and left out: it segments English differently too ("well-known" becomes
 * two words), so adopting it would change every English score to fix two locales. That
 * is a deliberate trade, not an oversight — Korean does use spaces and is fixed here.
 */
const WORD_SPLIT_REGEX = /[^\p{L}\p{N}\p{M}'-]+/u;
const DIGIT_REGEX = /\d/;
const LETTER_REGEX = /\p{L}/u;
const NON_WORD_TRIM_REGEX = /^[^\p{L}\p{N}\p{M}]+|[^\p{L}\p{N}\p{M}]+$/gu;

interface Factor {
  id: string;
  label: string;
  score: number;
  max: number;
  advice: string;
}

function getWords(headline: string): string[] {
  return headline
    .split(WORD_SPLIT_REGEX)
    .map((w) => w.replace(NON_WORD_TRIM_REGEX, ""))
    .filter((w) => w.length > 0);
}

/** English strings. A translated content file overrides these through `ui`. */
export const HEADLINE_ANALYZER_STRINGS = {
  yourHeadline: "Your headline",
  n7ProvenWaysToWrite: "7 Proven Ways to Write Headlines People Actually Click",
  character: "one: character|other: characters",
  word: "one: word|other: words",
  analyze: "Analyze",
  clear: "Clear",
  score: "Score: ",
  analyzed: "Analyzed: “{headline}”",
  totalScore: "Total score",
  characters: "Characters",
  words: "Words",
  factorBreakdown: "Factor Breakdown",
  lengthLabel: "Character length ({count})",
  lengthGreatAdvice:
    "Great — 50-60 characters fits search results without truncation.",
  lengthCloseAdvice:
    "Close to the sweet spot. Aim for 50-60 characters if you can.",
  lengthLongAdvice:
    "Long headlines get cut off in search and social feeds. Trim it.",
  lengthShortAdvice:
    "Short headlines can feel vague. Add a specific detail or benefit.",
  wordCountLabel: "Word count ({count})",
  wordCountGreatAdvice:
    "Great — 6-12 words is easy to scan and leaves room for detail.",
  wordCountWorkableAdvice:
    "Workable, but 6-12 words is the most comfortable band.",
  wordCountTooManyAdvice:
    "Too many words dilutes the message. Cut filler words.",
  wordCountVeryShortAdvice:
    "Very short. Add a concrete benefit or detail to earn the click.",
  powerWordsLabel: "Power words ({count})",
  powerWordsFoundAdvice:
    "Found: {words}. One or two is plenty — more starts to feel like clickbait.",
  powerWordsNoneAdvice:
    "No power words found. Words like proven, essential, or ultimate signal concrete value.",
  emotionalWordsLabel: "Emotional words ({count})",
  emotionalWordsFoundAdvice:
    "Found: {words}. Emotional words trigger curiosity and feeling.",
  emotionalWordsNoneAdvice:
    "No emotional words found. A word like surprising or inspiring can add pull — if it is honest.",
  containsANumber: "Contains a number",
  noNumber: "No number",
  numbersFoundAdvice:
    "Numbers promise concrete, scannable content — listicle headlines earn strong click-through.",
  numbersNoneAdvice:
    "Consider a number ('7 Ways to...', 'in 5 Minutes'). Specificity outperforms vague claims.",
  questionForm: "Question form",
  statementForm: "Statement form",
  questionAdvice:
    "Questions invite the reader to answer in their head — good for curiosity.",
  statementAdvice:
    "Statements are fine. A question form can add curiosity, but only if it is genuine.",
  clarity: "Clarity",
  clarityCleanAdvice: "Clean and readable — no shouting, no repeated keywords.",
  clarityAllCapsAdvice:
    "ALL CAPS reads as shouting and can hurt trust. Use normal casing.",
  clarityStuffedAdvice:
    "A word repeats three or more times — that reads as keyword stuffing. Vary your phrasing.",
  verdictStrong: "Strong headline",
  verdictDecent: "Decent — room to improve",
  verdictNeedsWork: "Needs work",
} as const;

type Strings = typeof HEADLINE_ANALYZER_STRINGS;

function scoreLength(charCount: number, strings: Strings): Factor {
  let score = 0;
  let advice = "";
  if (charCount >= 50 && charCount <= 60) {
    score = 20;
    advice = strings.lengthGreatAdvice;
  } else if (charCount >= 40 && charCount <= 70) {
    score = 14;
    advice = strings.lengthCloseAdvice;
  } else if (charCount > 70) {
    score = 6;
    advice = strings.lengthLongAdvice;
  } else if (charCount > 0) {
    score = 6;
    advice = strings.lengthShortAdvice;
  }
  return {
    id: "length",
    label: t(strings.lengthLabel, { count: charCount }),
    score,
    max: 20,
    advice,
  };
}

function scoreWordCount(wordCount: number, strings: Strings): Factor {
  let score = 0;
  let advice = "";
  if (wordCount >= 6 && wordCount <= 12) {
    score = 20;
    advice = strings.wordCountGreatAdvice;
  } else if (wordCount >= 4 && wordCount <= 15) {
    score = 13;
    advice = strings.wordCountWorkableAdvice;
  } else if (wordCount > 15) {
    score = 5;
    advice = strings.wordCountTooManyAdvice;
  } else if (wordCount > 0) {
    score = 5;
    advice = strings.wordCountVeryShortAdvice;
  }
  return {
    id: "words",
    label: t(strings.wordCountLabel, { count: wordCount }),
    score,
    max: 20,
    advice,
  };
}

function scorePowerWords(words: string[], strings: Strings): Factor {
  const hits = words.filter((w) => POWER_WORDS.has(w.toLowerCase()));
  let score = 0;
  if (hits.length >= 2) {
    score = 15;
  } else if (hits.length === 1) {
    score = 10;
  }
  const advice =
    hits.length > 0
      ? t(strings.powerWordsFoundAdvice, {
          words: [...new Set(hits.map((h) => h.toLowerCase()))].join(", "),
        })
      : strings.powerWordsNoneAdvice;
  return {
    id: "power",
    label: t(strings.powerWordsLabel, { count: hits.length }),
    score,
    max: 15,
    advice,
  };
}

function scoreEmotionalWords(words: string[], strings: Strings): Factor {
  const hits = words.filter((w) => EMOTIONAL_WORDS.has(w.toLowerCase()));
  let score = 0;
  if (hits.length >= 1) {
    score = 15;
  }
  const advice =
    hits.length > 0
      ? t(strings.emotionalWordsFoundAdvice, {
          words: [...new Set(hits.map((h) => h.toLowerCase()))].join(", "),
        })
      : strings.emotionalWordsNoneAdvice;
  return {
    id: "emotion",
    label: t(strings.emotionalWordsLabel, { count: hits.length }),
    score,
    max: 15,
    advice,
  };
}

function scoreNumbers(headline: string, strings: Strings): Factor {
  const hasNumber = DIGIT_REGEX.test(headline);
  return {
    id: "numbers",
    label: hasNumber ? strings.containsANumber : strings.noNumber,
    score: hasNumber ? 10 : 0,
    max: 10,
    advice: hasNumber ? strings.numbersFoundAdvice : strings.numbersNoneAdvice,
  };
}

function scoreQuestion(headline: string, strings: Strings): Factor {
  const isQuestion = headline.trim().endsWith("?");
  return {
    id: "question",
    label: isQuestion ? strings.questionForm : strings.statementForm,
    score: isQuestion ? 5 : 2,
    max: 5,
    advice: isQuestion ? strings.questionAdvice : strings.statementAdvice,
  };
}

function scoreClarity(
  headline: string,
  words: string[],
  strings: Strings
): Factor {
  const letters = headline.replace(NON_WORD_TRIM_REGEX, "");
  /*
   * "Is it shouting" is only a question in a script that HAS two cases.
   *
   * The old `hasLetters` test was `/[a-z]/i`, which excluded every non-Latin script and
   * so never reached the comparison. Widening it to `\p{L}` without this second test
   * would flag every Japanese, Chinese, Korean, Arabic and Hindi headline as ALL CAPS —
   * `toUpperCase()` is the identity function in a unicameral script, so the equality is
   * trivially true — and dock 12 of its 15 clarity points for shouting it cannot do.
   * Requiring the text to actually change when lowercased is what "has case" means.
   */
  const hasCase =
    LETTER_REGEX.test(letters) && letters !== letters.toLowerCase();
  const isAllCaps = hasCase && letters === letters.toUpperCase();
  const counts = new Map<string, number>();
  for (const w of words) {
    const key = w.toLowerCase();
    if (key.length >= 4) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const stuffed = [...counts.entries()].some(([, n]) => n >= 3);
  let score = 15;
  let advice: string = strings.clarityCleanAdvice;
  if (isAllCaps) {
    score = 3;
    advice = strings.clarityAllCapsAdvice;
  } else if (stuffed) {
    score = 6;
    advice = strings.clarityStuffedAdvice;
  }
  return { id: "clarity", label: strings.clarity, score, max: 15, advice };
}

function analyzeHeadline(headline: string, strings: Strings): Factor[] {
  const words = getWords(headline);
  return [
    scoreLength(headline.trim().length, strings),
    scoreWordCount(words.length, strings),
    scorePowerWords(words, strings),
    scoreEmotionalWords(words, strings),
    scoreNumbers(headline, strings),
    scoreQuestion(headline, strings),
    scoreClarity(headline, words, strings),
  ];
}

function meterColorClass(total: number): string {
  if (total >= 75) {
    return "bg-green-500";
  }
  if (total >= 50) {
    return "bg-yellow-500";
  }
  return "bg-red-500";
}

function scoreVerdict(total: number, strings: Strings): string {
  if (total >= 75) {
    return strings.verdictStrong;
  }
  if (total >= 50) {
    return strings.verdictDecent;
  }
  return strings.verdictNeedsWork;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function HeadlineAnalyzerWidget() {
  const [headline, setHeadline] = useState("");
  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [analyzedHeadline, setAnalyzedHeadline] = useState("");
  const locale = useToolLocale();
  const strings = useToolUi(HEADLINE_ANALYZER_STRINGS);

  const charCount = headline.trim().length;
  const wordCount = getWords(headline).length;
  const total = factors ? factors.reduce((sum, f) => sum + f.score, 0) : 0;

  function handleAnalyze() {
    if (headline.trim().length === 0) {
      return;
    }
    setFactors(analyzeHeadline(headline, strings));
    setAnalyzedHeadline(headline.trim());
  }

  function handleClear() {
    setHeadline("");
    setFactors(null);
    setAnalyzedHeadline("");
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="headline-input"
        >
          {strings.yourHeadline}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="headline-input"
          onChange={(e) => setHeadline(e.target.value)}
          placeholder={strings.n7ProvenWaysToWrite}
          type="text"
          value={headline}
        />
        <p className="text-muted-foreground text-xs">
          {charCount}
          {tPlural(strings.character, locale, charCount)} · {wordCount}
          {tPlural(strings.word, locale, wordCount)}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={headline.trim().length === 0}
          onClick={handleAnalyze}
          type="button"
        >
          {strings.analyze}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={headline.length === 0 && factors === null}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Score meter */}
      {factors !== null && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-6">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.score}
              {total}/100
            </h2>
            <p className="text-muted-foreground text-sm">
              {scoreVerdict(total, strings)}
            </p>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full transition-all ${meterColorClass(total)}`}
              style={{ width: `${total}%` }}
            />
          </div>
          <p className="text-muted-foreground text-xs">
            {t(strings.analyzed, { headline: analyzedHeadline })}
          </p>
        </section>
      )}
      {/* Stats */}
      {factors !== null && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.totalScore} value={`${total}/100`} />
          <StatCard
            label={strings.characters}
            value={analyzedHeadline.length}
          />
          <StatCard
            label={strings.words}
            value={getWords(analyzedHeadline).length}
          />
        </div>
      )}
      {/* Factor breakdown */}
      {factors !== null && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.factorBreakdown}
          </h2>
          <div className="space-y-4">
            {factors.map((f) => (
              <div
                className="rounded-lg border border-border bg-card p-5"
                key={f.id}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-medium text-foreground">{f.label}</p>
                  <p className="text-muted-foreground text-sm">
                    {f.score}/{f.max}
                  </p>
                </div>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full ${meterColorClass((f.score / f.max) * 100)}`}
                    style={{ width: `${(f.score / f.max) * 100}%` }}
                  />
                </div>
                <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                  {f.advice}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
